import React, {Component} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Card, Form, Input, InputNumber, notification, Radio} from 'antd';

import {takeStockTake, getStock, clearStock, getStockTakeCounts} from './actions';
import {getDescriptionFromError} from "../utils/errors";
import {formItemLayout} from "../utils/style";
import {bindActionCreators} from "redux";
import {getProduct} from "../Product/actions";
import {decodeParams} from "../utils/params";

const FormItem = Form.Item;
const ButtonGroup = Button.Group;

function hasErrors(fieldsError) {
  return Object.keys(fieldsError).some(field => fieldsError[field]);
}

class TakeStockTakeForm extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired,
    /* actions */
    takeStockTake: PropTypes.func.isRequired,
    getStock: PropTypes.func.isRequired,
    getProduct: PropTypes.func.isRequired,
    clearStock: PropTypes.func.isRequired,
    getStockTakeCounts: PropTypes.func.isRequired,
    /* state */
    dispensaryId: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    form: PropTypes.object.isRequired,
    stock: PropTypes.object,
    product: PropTypes.object,
    stockTakeCounts: PropTypes.array.isRequired,
  };

  componentDidMount() {
    const {clearStock, match: {params}} = this.props;
    const {sku} = decodeParams(params);
    clearStock();
    if (sku) {
      this._loadSku(sku).finally(() => {});
    }
  }

  _loadSku = async(sku) => {
    const {dispensaryId, getStock, getProduct, getStockTakeCounts, match: {params}} = this.props;
    const {stockTakeId} = decodeParams(params);
    try {
      let productId = '';
      if (sku) {
        await getStock({dispensaryId, sku});
        productId = this.props.stock.productId;
      }
      await getProduct({productId});
      await getStockTakeCounts({stockTakeId, sku});
    } catch (err) {
      //
    }
  };

  goBack = () => {
    this.props.history.goBack();
  };

  handleSubmit = (e) => {
    e.preventDefault();
    const {match: {params}, form: {setFieldsValue}} = this.props;
    const {stockTakeId, sku} = decodeParams(params);
    this.props.form.validateFields(async(err, {binId, ...values}) => {
      if (err) return;
      try {
        await this.props.takeStockTake({...values, stockTakeId, binId: binId || null});
        setFieldsValue({sku: undefined, quantity: undefined});
        this.props.clearStock();
        if (sku) {
          this.goBack();
        } else {
          this.skuInput.focus();
        }
      } catch (err) {
        notification.error({
          message: 'Failed',
          description: getDescriptionFromError(err)
        });
      }
    });
  };

  handleSkuChange = (e) => {
    if (this.timeoutHandle) {
      clearTimeout(this.timeoutHandle);
    }
    this.timeoutHandle = setTimeout(async(sku) => {
      delete this.timeoutHandle;
      await this._loadSku(sku);
    }, 300, e.target.value);
  };

  render() {
    const {
      match: {params},
      form: {getFieldDecorator, getFieldsError, getFieldValue},
      stock, product, stockTakeCounts,
    } = this.props;
    const {sku} = decodeParams(params);
    const showProduct = !!(stock && product);
    const quantityDefined = getFieldValue('quantity') !== undefined;
    const {bins, nextBinId} = stockTakeCounts.reduce(
      (
        {bins, nextBinId},
        {binId, quantity}
      ) => {
        if (binId) {
          bins = {...bins, [binId]: quantity};
          nextBinId = (Number(binId) + 1).toString();
        }
        return {bins, nextBinId};
      },
      {bins: {}, nextBinId: '1'}
    );
    const radioStyle = {
      display: 'block',
      height: '2em',
      lineHeight: '2em',
    };
    return (
      <Card title={[
        <Button key="back" icon="arrow-left" onClick={this.goBack} />,
        " Take Stock-take"
      ]}>
        <Form onSubmit={this.handleSubmit}>
          <FormItem
            {...formItemLayout}
            label="SKU"
          >
            {getFieldDecorator('sku', {
              initialValue: sku || '',
              rules: [{
                required: true,
                message: 'SKU is required'
              }]
            })(
              <Input ref={el => this.skuInput = el} autoFocus={!showProduct} onChange={this.handleSkuChange} />
            )}
          </FormItem>
          {showProduct && [
            <FormItem
              key="product"
              {...formItemLayout}
              label="Product"
            >
              {product.display}
            </FormItem>,
            <FormItem
              key="quantity"
              {...formItemLayout}
              label="Quantity"
            >
              {getFieldDecorator('quantity', {
                initialValue: '',
                rules: [{
                  validator(rule, value, callback) {
                    const errors = [];
                    const n = Number(value);
                    if (value === '' || n < 0 || n !== Number(n.toFixed())) {
                      errors.push(new Error('must be a non-negative whole number'));
                    }
                    callback(errors);
                  }
                }]
              })(
                <InputNumber type="number" autoFocus={showProduct} step={1} precision={0} min={0} />
              )}
            </FormItem>,
            <FormItem
              key="action"
              {...formItemLayout}
              label="Action"
            >
              {getFieldDecorator('binId', {initialValue: nextBinId})(
                <Radio.Group>
                  <Radio value={nextBinId} style={radioStyle}>Add</Radio>
                  {Object.entries(bins).map(([id, quantity]) =>
                    <Radio key={id} value={id} style={radioStyle}>Replace #{id}: {quantity}</Radio>
                  )}
                  {Object.values(bins).length > 0 &&
                  <Radio style={radioStyle}>Total</Radio>
                  }
                </Radio.Group>
              )}
            </FormItem>,
          ]}
          <FormItem>
            <ButtonGroup>
              <Button
                type="primary"
                htmlType="submit"
                disabled={!quantityDefined || hasErrors(getFieldsError())}
              >
                Submit
              </Button>
              <Button
                htmlType="button"
                onClick={this.goBack}
              >
                Cancel
              </Button>
            </ButtonGroup>
          </FormItem>
        </Form>
      </Card>
    );
  }
}

const TakeStockTakePage = Form.create()(TakeStockTakeForm);

const mapStateToProps = state => ({
  dispensaryId: state.dispensary.selectedDispensaryId,
  loading: state.stockTake.takingStockTake || state.stockTake.gettingStock || state.stockTake.gettingStockTakeCounts,
  stock: state.stockTake.stock,
  product: state.product.product,
  stockTakeCounts: state.stockTake.stockTakeCounts,
});

const mapDispatchToProps = dispatch => (bindActionCreators({
  takeStockTake,
  getStock,
  getProduct,
  clearStock,
  getStockTakeCounts,
}, dispatch));

export default connect(mapStateToProps, mapDispatchToProps)(TakeStockTakePage);



// WEBPACK FOOTER //
// ./src/StockTake/TakeStockTakePage.js